import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { buildApiUrl, buildPublicFileUrl } from "../config/api";
import ResponsiveImage from "./ResponsiveImage";
import "./ProductCard.css";
const CARD_FALLBACK_IMAGE = "/images/mayachitro-golden-river-boat-sunset-hero.jpg";

const normalizeUploadPath = (value) =>
  String(value || "").replace(
    /\/uploads\/public\/products\/mayachitro\/photo-downloads\//gi,
    "/uploads/public/products/"
  );

const getField = (item, names, fallback = "") => {
  for (const name of names) {
    if (
      item &&
      item[name] !== undefined &&
      item[name] !== null &&
      item[name] !== ""
    ) {
      return item[name];
    }
  }

  return fallback;
};

const parseJsonArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value !== "string" || !value.trim()) return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
};

const toAssetUrl = (path) => {
  if (!path || typeof path !== "string") return CARD_FALLBACK_IMAGE;

  path = normalizeUploadPath(path);

  if (path.startsWith("data:")) return path;

  if (/^(https?:)?\/\//i.test(path)) {
    try {
      const url = new URL(path, window.location.origin);

      if (url.pathname.startsWith("/uploads/public/")) return path;

      return path;
    } catch {
      return path;
    }
  }

  if (path.startsWith("/uploads") || path.startsWith("uploads/")) {
    return buildPublicFileUrl(path);
  }

  return path;
};

const toNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const formatPrice = (value) => {
  const number = toNumber(value, 0);
  return number.toFixed(2);
};

const stripExtension = (value) => {
  return String(value || "")
    .trim()
    .replace(/\.(avif|webp|jpg|jpeg|png)$/i, "");
};

const stripVariantSuffix = (value) => {
  return stripExtension(value)
    .replace(/-card$/i, "")
    .replace(/-thumb$/i, "");
};

const getFileBaseFromPath = (path) => {
  if (!path || typeof path !== "string") return "";

  const cleanPath = path.split("?")[0].split("#")[0];
  const fileName = cleanPath.split("/").pop() || "";

  return stripVariantSuffix(fileName);
};

const getFolderName = (product) => {
  return getField(product, [
    "folder_name",
    "folderName",
    "image_folder",
    "imageFolder",
    "product_folder",
    "productFolder",
    "folder",
    "publicBasePath",
    "public_base_path",
  ]);
};

const getMainImageBase = (product) => {
  const databaseBase = getField(product, [
    "main_image_base",
    "mainImageBase",
    "image_base",
    "imageBase",
  ]);

  if (databaseBase) return stripVariantSuffix(databaseBase);

  const mainImagePath = getField(product, [
    "mainImage",
    "main_image",
    "thumbnailImage",
    "thumbnail_image",
    "thumbnail",
    "image",
    "previewImage",
    "preview_image",
    "coverImage",
    "cover_image",
  ]);

  const fileBase = getFileBaseFromPath(mainImagePath);

  return fileBase || "main";
};

const getHoverImageBase = (product) => {
  const galleryBases = parseJsonArray(
    getField(product, [
      "gallery_image_bases",
      "galleryImageBases",
      "galleryBases",
      "gallery_bases",
    ])
  );

  if (galleryBases.length > 0) {
    return stripVariantSuffix(galleryBases[0]);
  }

  const previewImages = parseJsonArray(
    getField(product, [
      "previewImages",
      "preview_images",
      "previewImagesJson",
      "preview_images_json",
    ])
  );

  const mockups = parseJsonArray(
    getField(product, ["mockups", "mockupsJson", "mockups_json"])
  );

  const firstHoverPath = [...mockups, ...previewImages].find(Boolean);

  return getFileBaseFromPath(firstHoverPath);
};

const getFallbackImage = (product) => {
  const fallbackPath = getField(product, [
    "thumbnailImage",
    "thumbnail_image",
    "thumbnail",
    "image",
    "previewImage",
    "preview_image",
    "coverImage",
    "cover_image",
    "mainImage",
    "main_image",
  ]);

  return fallbackPath ? toAssetUrl(fallbackPath) : CARD_FALLBACK_IMAGE;
};

const getPricing = (product) => {
  const salePrice = toNumber(
    getField(product, [
      "salePrice",
      "sale_price",
      "personalSalePrice",
      "personal_sale_price",
      "personalPrice",
      "personal_price",
      "price",
    ]),
    0
  );

  const explicitRegularPrice = toNumber(
    getField(product, [
      "regularPrice",
      "regular_price",
      "originalPrice",
      "original_price",
      "compareAtPrice",
      "compare_at_price",
      "listPrice",
      "list_price",
    ]),
    0
  );

  const regularPrice = explicitRegularPrice > salePrice ? explicitRegularPrice : salePrice;

  const explicitDiscount = toNumber(
    getField(product, ["discountPercent", "discount_percent", "discount"], 0),
    0
  );

  const discountPercent =
    explicitDiscount > 0
      ? Math.round(explicitDiscount)
      : explicitRegularPrice > salePrice && regularPrice > 0
      ? Math.round(((regularPrice - salePrice) / regularPrice) * 100)
      : 0;

  return {
    salePrice,
    regularPrice,
    discountPercent,
  };
};

const isSoldOutProduct = (product) => {
  const stockStatus = String(
    getField(product, ["stock_status", "stockStatus", "availability"], "")
  ).toLowerCase();

  const status = String(getField(product, ["status"], "")).toLowerCase();

  const soldOutValue = getField(
    product,
    ["sold_out", "soldOut", "is_sold_out", "isSoldOut"],
    false
  );

  return (
    soldOutValue === true ||
    soldOutValue === 1 ||
    soldOutValue === "1" ||
    stockStatus.includes("sold") ||
    stockStatus.includes("out") ||
    status === "sold_out"
  );
};

const resolveBadge = (product) => {
  if (isSoldOutProduct(product)) return "Sold Out";

  const rawBadge = String(getField(product, ["badge"], "")).trim();
  const lowerBadge = rawBadge.toLowerCase();

  const isBestSeller =
    getField(
      product,
      ["isBestSeller", "is_best_seller", "is_bestseller"],
      false
    ) === true ||
    getField(
      product,
      ["isBestSeller", "is_best_seller", "is_bestseller"],
      false
    ) === 1 ||
    lowerBadge.includes("best");

  if (isBestSeller) return "Bestseller";

  if (
    lowerBadge === "new" ||
    lowerBadge === "trending" ||
    lowerBadge.includes("trend")
  ) {
    return "";
  }

  return rawBadge;
};

const getDownloadLabel = (product) => {
  const productType = String(
    getField(product, ["product_type", "productType"], "")
  ).toLowerCase();

  if (productType.includes("printable")) return "Printable digital file";
  if (productType.includes("license")) return "License-ready image";

  return "Digital download";
};

const getProductBrandSlug = (product) => {
  return String(getField(product, ["brandSlug", "brand_slug", "brand"], ""))
    .trim()
    .toLowerCase();
};

const ProductCard = ({ product, compact = false, hidePrice = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn } = useAuth();

  const [isSaved, setIsSaved] = useState(false);
  const [wishlistBusy, setWishlistBusy] = useState(false);

  const imageData = useMemo(() => {
    const folderName = getFolderName(product);
    const mainImageBase = getMainImageBase(product);
    const hoverImageBase = getHoverImageBase(product);
    const previewImages = parseJsonArray(getField(product, ["previewImages", "preview_images", "previewImagesJson", "preview_images_json"]));
    const mockups = parseJsonArray(getField(product, ["mockups", "mockupsJson", "mockups_json"]));
    const hoverFallback = [...mockups, ...previewImages].find((path) => path && path !== getField(product, ["image", "mainImage", "main_image"]));
    const fallbackSrc = getFallbackImage(product);

    return {
      folderName,
      mainImageBase,
      hoverImageBase,
      fallbackSrc,
      hasOptimizedFolder: Boolean(folderName),
      hasHoverImage: Boolean(folderName && hoverImageBase),
      hoverFallback: hoverFallback ? toAssetUrl(hoverFallback) : "",
    };
  }, [product]);

  const slug = getField(product, ["slug"], "");
  const title = getField(product, ["title", "name"], "Untitled Product");
  const shortTitle = getField(product, ["shortTitle", "short_title"], title);
  const altText = getField(product, ["altText", "alt_text"], title);
  const brandSlug = getProductBrandSlug(product);
  const badgeLabel = resolveBadge(product);
  const pricing = getPricing(product);
  const downloadLabel = getDownloadLabel(product);
  const soldOut = isSoldOutProduct(product);
  const coverZoom = Math.min(2, Math.max(1, toNumber(getField(product, ["coverZoom", "cover_zoom"], 1), 1)));
  const coverPositionX = Math.min(100, Math.max(0, toNumber(getField(product, ["coverPositionX", "cover_position_x"], 50), 50)));
  const coverPositionY = Math.min(100, Math.max(0, toNumber(getField(product, ["coverPositionY", "cover_position_y"], 50), 50)));

  const productUrl = slug ? `/product/${slug}` : "/shop";

  const cardClass = [
    "product-card",
    compact ? "compact" : "",
    hidePrice ? "hide-price" : "",
    soldOut ? "sold-out" : "",
    brandSlug === "cloudy-pixel-studio" ? "cloudy" : "maya",
  ]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    let ignore = false;

    async function checkWishlistStatus() {
      if (!isLoggedIn || !slug) {
        setIsSaved(false);
        return;
      }

      try {
        const response = await fetch(
          buildApiUrl(`/wishlist/check/${encodeURIComponent(slug)}`),
          {
            method: "GET",
            credentials: "include",
            headers: {
              Accept: "application/json",
            },
          }
        );

        const data = await response.json();

        if (!ignore && response.ok && data.success) {
          setIsSaved(Boolean(data.saved));
        }
      } catch (error) {
        console.error("Wishlist check error:", error);
      }
    }

    checkWishlistStatus();

    return () => {
      ignore = true;
    };
  }, [isLoggedIn, slug]);

  const handleWishlistToggle = useCallback(
    async (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (!slug) return;

      if (!isLoggedIn) {
        navigate(
          `/login?redirect=${encodeURIComponent(
            location.pathname || "/wishlist"
          )}`
        );
        return;
      }

      try {
        setWishlistBusy(true);

        const response = await fetch(
          isSaved
            ? buildApiUrl(`/wishlist/${encodeURIComponent(slug)}`)
            : buildApiUrl("/wishlist"),
          {
            method: isSaved ? "DELETE" : "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: isSaved ? undefined : JSON.stringify({ productSlug: slug }),
          }
        );

        const data = await response.json();

        if (!response.ok || !data.success) {
          alert(data.message || "Could not update wishlist.");
          return;
        }

        const nextSavedState = !isSaved;
        setIsSaved(nextSavedState);

        window.dispatchEvent(
          new CustomEvent("mayachitro:wishlist-updated", {
            detail: {
              productSlug: slug,
              saved: nextSavedState,
            },
          })
        );
      } catch (error) {
        console.error("Wishlist update error:", error);
        alert("Could not update wishlist. Please try again.");
      } finally {
        setWishlistBusy(false);
      }
    },
    [isLoggedIn, isSaved, location.pathname, navigate, slug]
  );

  if (!product) return null;

  return (
    <article className={cardClass}>
      <div className="product-card-image-wrap">
        <Link
          to={productUrl}
          className="product-card-image-link"
          aria-label={`View ${title}`}
        >
          {badgeLabel && <span className="product-badge">{badgeLabel}</span>}

          {imageData.hasOptimizedFolder ? (
            <ResponsiveImage
              product={product}
              folderName={imageData.folderName}
              imageBase={imageData.mainImageBase}
              variant="card"
              alt={altText}
              className="product-card-img product-card-img-main"
              imgClassName="product-card-img-inner"
              fallbackSrc={imageData.fallbackSrc}
              objectFit="cover"
              objectPosition={`${coverPositionX}% ${coverPositionY}%`}
              imgStyle={{
                transform: `scale(${coverZoom})`,
                transformOrigin: `${coverPositionX}% ${coverPositionY}%`,
              }}
              sizes="(max-width: 520px) 50vw, (max-width: 900px) 33vw, (max-width: 1280px) 25vw, 20vw"
            />
          ) : (
            <img
              className="product-card-img product-card-img-main"
              src={imageData.fallbackSrc}
              alt={altText}
              loading="lazy"
              onError={(event) => {
                event.currentTarget.src = CARD_FALLBACK_IMAGE;
              }}
            />
          )}

          {imageData.hoverFallback ? (
            <img
              className="product-card-img product-card-img-hover"
              src={imageData.hoverFallback}
              alt=""
              loading="lazy"
              aria-hidden="true"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          ) : imageData.hasHoverImage && (
            <ResponsiveImage
              product={product}
              folderName={imageData.folderName}
              imageBase={imageData.hoverImageBase}
              variant="card"
              alt=""
              className="product-card-img product-card-img-hover"
              imgClassName="product-card-img-inner"
              fallbackSrc={imageData.fallbackSrc}
              objectFit="cover"
              aria-hidden="true"
              sizes="(max-width: 520px) 50vw, (max-width: 900px) 33vw, (max-width: 1280px) 25vw, 20vw"
            />
          )}

          <span className="product-card-view-badge">View</span>
        </Link>

        <button
          type="button"
          className={`product-wishlist-btn ${isSaved ? "saved" : ""}`}
          onClick={handleWishlistToggle}
          disabled={wishlistBusy}
          aria-label={
            isSaved
              ? `Remove ${title} from wishlist`
              : `Save ${title} to wishlist`
          }
          title={isSaved ? "Remove from wishlist" : "Save to wishlist"}
        >
          {wishlistBusy ? "…" : isSaved ? "♥" : "♡"}
        </button>
      </div>

      <Link
        to={productUrl}
        className="product-card-body-link"
        aria-label={`View ${title}`}
      >
        <div className="product-card-body">
          <p className="product-card-category">
            {getField(product, ["category", "categoryName", "category_name"], "Original photography")}
          </p>
          <h3 title={title}>{shortTitle}</h3>

          {!hidePrice && (
            <div className="product-card-selling-row">
              <strong className="product-card-sale-price">
                USD {formatPrice(pricing.salePrice)}
              </strong>

              {pricing.regularPrice > pricing.salePrice && (
                <span className="product-card-regular-price">
                  USD {formatPrice(pricing.regularPrice)}
                </span>
              )}

              {pricing.discountPercent > 0 && (
                <span className="product-card-discount">
                  {pricing.discountPercent}% off
                </span>
              )}
            </div>
          )}

          <div className="product-card-footer-line">
            <div className="product-card-download">
              <span aria-hidden="true">⇩</span>
              {downloadLabel}
            </div>

            <span className="product-card-open">Open →</span>
          </div>
        </div>
      </Link>
    </article>
  );
};

export default ProductCard;
